import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useAuth } from '../auth/AuthContext'
import { AppShell } from '../components/AppShell'
import { Field } from '../components/Field'
import { TotpSection } from '../components/profile/TotpSection'
import { SessionsSection } from '../components/profile/SessionsSection'
import { TrustedDevicesSection } from '../components/profile/TrustedDevicesSection'

/** Настройки учителя: ФИО, локаль, тема, LLM-провайдер. */
export function ProfilePage() {
  const { t } = useTranslation()
  const { teacher, updateProfile } = useAuth()
  const [displayName, setDisplayName] = useState(teacher?.displayName ?? '')
  const [theme, setTheme] = useState(teacher?.theme ?? 'SYSTEM')
  const [llmProvider, setLlmProvider] = useState(teacher?.preferredLlmProvider ?? 'OWN_KEY')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  if (!teacher) return null

  const dirty =
    displayName !== teacher.displayName ||
    theme !== teacher.theme ||
    llmProvider !== teacher.preferredLlmProvider

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await updateProfile({ displayName, theme, preferredLlmProvider: llmProvider })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <AppShell title={t('profilePage.title')} subtitle={t('profilePage.subtitle')}>
      <div className="max-w-2xl space-y-6">
        {/* Identity */}
        <Section title={t('profilePage.sectionIdentity')}>
          <Field label={t('profilePage.fullName')}>
            <input
              className="input-field"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </Field>
          <Field label={t('profilePage.email')}>
            <input className="input-field bg-gray-50" value={teacher.email} disabled />
            <p className="text-xs text-gray-500 mt-1">
              {t('profilePage.emailHint')}{' '}
              <a className="text-primary hover:underline" href="mailto:info@smart-life.pro">
                info@smart-life.pro
              </a>
              .
            </p>
          </Field>
          <Field label={t('profilePage.role')}>
            <input
              className="input-field bg-gray-50"
              value={roleLabel(teacher.role, t)}
              disabled
            />
          </Field>
        </Section>

        {/* Appearance */}
        <Section title={t('profilePage.sectionAppearance')}>
          <Field label={t('profilePage.theme')}>
            <select
              className="input-field"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
            >
              <option value="SYSTEM">{t('profilePage.themeSystem')}</option>
              <option value="LIGHT">{t('profilePage.themeLight')}</option>
              <option value="DARK">{t('profilePage.themeDark')}</option>
            </select>
          </Field>
        </Section>

        {/* LLM */}
        <Section title={t('profilePage.sectionLlm')}>
          <Field label={t('profilePage.llmSource')}>
            <select
              className="input-field"
              value={llmProvider}
              onChange={(e) => setLlmProvider(e.target.value)}
            >
              <option value="OWN_KEY">{t('profilePage.llmOwnKey')}</option>
              <option value="SCHOOL_PROXY">{t('profilePage.llmSchoolProxy')}</option>
              <option value="MOCK">{t('profilePage.llmMock')}</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {t('profilePage.llmHint')}
            </p>
          </Field>
        </Section>

        {/* Security */}
        <Section title={t('profilePage.section2fa')}>
          <TotpSection />
        </Section>

        <Section title={t('profilePage.sectionSessions')}>
          <SessionsSection />
        </Section>

        <Section title={t('trustedDevices.title')}>
          <TrustedDevicesSection />
        </Section>

        {/* Account */}
        <Section title={t('profilePage.sectionAccount')}>
          <div className="text-sm text-gray-600 space-y-1">
            <div>ID: <code className="text-xs">{teacher.id}</code></div>
            <div>{t('profilePage.accountSchool')} {teacher.schoolId ? <code className="text-xs">{teacher.schoolId}</code> : t('profilePage.accountNoSchool')}</div>
            <div>{t('profilePage.accountCreated')} {new Date(teacher.createdAt).toLocaleString('ru')}</div>
            <div>{t('profilePage.accountUpdated')} {new Date(teacher.updatedAt).toLocaleString('ru')}</div>
          </div>
        </Section>

        {/* Save bar — обычный в конце, не sticky (на mobile sticky bottom конфликтует с cookie banner) */}
        <div className="bg-white border border-gray-200 rounded-xl shadow px-4 py-3 flex items-center gap-3 mt-6">
          {saved && (
            <span className="text-sm text-green-600">✓ {t('profilePage.saved')}</span>
          )}
          <div className="flex-1" />
          <button
            disabled={!dirty || saving}
            onClick={handleSave}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? t('profilePage.saving') : t('profilePage.saveChanges')}
          </button>
        </div>
        {/* Доп. высота снизу чтобы cookie banner не закрывал последний контент */}
        <div className="h-24 md:h-0" />
      </div>
    </AppShell>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h2 className="font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </div>
  )
}

function roleLabel(role: string, t: TFunction): string {
  const key = {
    TEACHER: 'roleTeacher',
    VICE_PRINCIPAL: 'roleVicePrincipal',
    PRINCIPAL: 'rolePrincipal',
    IT_ADMIN: 'roleItAdmin',
    ADMIN: 'roleAdmin',
  }[role]
  return key ? t(`profilePage.${key}`) : role
}
